// Dev-only Operations hub. Merges the event log, live status, and (Phase 4)
// host diagnostics into one page. Gated to the `dev` role by the route guard
// in main.tsx and the nav filter in Layout.tsx.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Copy,
  Cpu,
  HeartPulse,
  Pause,
  Play,
  RefreshCw,
  ScrollText,
} from "lucide-react";
import {
  api,
  EventFilters,
  EventRow,
  EventSeverity,
  HostChecklistItem,
  HostContainer,
} from "../api";
import Status from "./Status";

type Tab = "events" | "status" | "diagnostics" | "model";

const TABS: Array<{ id: Tab; label: string; icon: typeof ScrollText }> = [
  { id: "events", label: "Event log", icon: ScrollText },
  { id: "status", label: "Live status", icon: Activity },
  { id: "diagnostics", label: "Host diagnostics", icon: HeartPulse },
  { id: "model", label: "Captioning model", icon: Cpu },
];

export default function Operations() {
  const [tab, setTab] = useState<Tab>("events");

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-white">Operations</h1>
        <p className="text-sm text-bf-muted">
          Check that the system is running and look into problems.
        </p>
      </header>

      <div className="flex flex-wrap gap-1 border-b border-white/5">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={
              "flex items-center gap-2 rounded-t-md px-3 py-2 text-sm transition " +
              (tab === id
                ? "bg-white/5 text-bf-text"
                : "text-bf-muted hover:text-bf-text")
            }
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "events" && <EventLogTab />}
      {tab === "status" && <Status embedded />}
      {tab === "diagnostics" && <HostDiagnosticsTab />}
      {tab === "model" && <CaptionModelTab />}
    </div>
  );
}

// ---------------- Captioning model tab ----------------

function CaptionModelTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["caption-models"],
    queryFn: () => api.captionModels.list(),
    refetchInterval: 5000,
  });
  const activate = useMutation({
    mutationFn: (id: string) => api.captionModels.activate(id),
    onSettled: () => qc.invalidateQueries({ queryKey: ["caption-models"] }),
  });
  const data = q.data;
  const loading = data ? !data.served.includes(data.configured_model) : false;

  return (
    <div className="space-y-4">
      <section className="bf-card space-y-1 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-bf-muted">Captions use</span>
          <code className="text-bf-text">{data?.configured_model ?? "…"}</code>
          <span
            className={
              "rounded px-1.5 py-0.5 text-xs " +
              (loading ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300")
            }
          >
            {data ? (loading ? "Loading in vLLM" : "Serving") : ""}
          </span>
        </div>
        <p className="text-bf-muted">
          Switching restarts vLLM; captions pause until the new model has loaded
          (several minutes the first time a model is downloaded).
        </p>
      </section>
      {q.isError && (
        <div className="bf-card border border-red-500/30 bg-red-500/5 text-sm text-red-300">
          {(q.error as Error).message}
        </div>
      )}
      {activate.isError && (
        <div className="bf-card border border-red-500/30 bg-red-500/5 text-sm text-red-300">
          {(activate.error as Error).message}
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {data?.models.map((m) => {
          const active =
            data.active_id === m.id || (!data.active_id && data.configured_model === m.model);
          return (
            <section
              key={m.id}
              className={"bf-card space-y-2 " + (active ? "border border-brand-purple/50" : "")}
            >
              <div className="font-semibold text-white">{m.label || m.id}</div>
              <code className="block break-all text-xs text-bf-muted">{m.model}</code>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-bf-muted">
                <dt>Precision</dt>
                <dd className="text-bf-text">{m.quant || "checkpoint"}</dd>
                <dt>GPU share</dt>
                <dd className="text-bf-text">{Math.round(m.gpu_mem * 100)}%</dd>
                <dt>Context</dt>
                <dd className="text-bf-text">{m.max_model_len.toLocaleString()} tokens</dd>
                <dt>Concurrent</dt>
                <dd className="text-bf-text">{m.max_num_seqs}</dd>
              </dl>
              <button
                type="button"
                className="bf-button w-full py-1.5 text-sm"
                disabled={active || activate.isPending}
                onClick={() => {
                  if (window.confirm(`Restart vLLM with ${m.label || m.model}?`)) {
                    activate.mutate(m.id);
                  }
                }}
              >
                {active ? "In use" : activate.isPending ? "Switching…" : "Use this model"}
              </button>
            </section>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Host diagnostics tab ----------------

const VERDICT_STYLES: Record<string, string> = {
  clean: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
  abrupt: "border-red-500/30 bg-red-500/5 text-red-300",
  unknown: "border-white/10 text-bf-muted",
};

function checkTone(ok: boolean | null): string {
  if (ok === true) return "text-emerald-400";
  if (ok === false) return "text-amber-300";
  return "text-bf-muted";
}

function ChecklistCard({
  title,
  items,
}: {
  title: string;
  items: HostChecklistItem[] | undefined;
}) {
  return (
    <section className="bf-card space-y-2">
      <h2 className="text-sm font-medium">{title}</h2>
      {(items ?? []).map((it) => (
        <div
          key={it.id}
          className="flex items-start justify-between gap-3 border-t border-white/5 py-2 first:border-t-0"
        >
          <div className="min-w-0">
            <div className="text-sm">{it.label}</div>
            <div className="text-xs text-bf-muted">{it.detail}</div>
          </div>
          <div className={"shrink-0 font-mono text-xs " + checkTone(it.ok)}>
            {it.value === null || it.value === undefined
              ? "—"
              : Array.isArray(it.value)
                ? it.value.length
                  ? it.value.join(", ")
                  : "none"
                : String(it.value)}
          </div>
        </div>
      ))}
    </section>
  );
}

function ContainersCard({ containers }: { containers: HostContainer[] | undefined }) {
  return (
    <section className="bf-card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
          <tr>
            <th className="p-3">Container</th>
            <th className="p-3">Status</th>
            <th className="p-3">Restarts</th>
            <th className="p-3">Exit</th>
            <th className="p-3">Started</th>
          </tr>
        </thead>
        <tbody>
          {(containers ?? []).map((c) => (
            <tr key={c.container} className="border-t border-white/5">
              <td className="p-3 font-mono text-xs">{c.container}</td>
              <td className="p-3">
                <span
                  className={
                    c.status === "running"
                      ? "text-emerald-400"
                      : c.status === "absent"
                        ? "text-bf-muted"
                        : "text-amber-300"
                  }
                >
                  {c.status ?? "—"}
                </span>
              </td>
              <td
                className={
                  "p-3 " + ((c.restart_count ?? 0) > 3 ? "text-amber-300" : "")
                }
              >
                {c.restart_count ?? "—"}
                {c.oom_killed ? " (OOM)" : ""}
              </td>
              <td className="p-3">{c.exit_code ?? "—"}</td>
              <td className="p-3 text-xs text-bf-muted">
                {c.started_at ? new Date(c.started_at).toLocaleString() : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function CrashWindowCard() {
  const q = useQuery({
    queryKey: ["ops-crash-window"],
    queryFn: () => api.operations.crashWindow({ window_min: 15 }),
  });
  const series = q.data?.series ?? {};
  const summarize = (pts: Array<[number, number]> | undefined) => {
    if (!pts || pts.length === 0) return { last: null, min: null, max: null };
    const vals = pts.map((p) => p[1]);
    return {
      last: vals[vals.length - 1],
      min: Math.min(...vals),
      max: Math.max(...vals),
    };
  };
  const labels: Record<string, string> = {
    xid_errors: "GPU Xid errors",
    gpu_temp_c: "GPU temp (°C)",
    fb_used_mib: "VRAM used (MiB)",
    gpu_util_pct: "GPU util (%)",
    power_w: "Power (W)",
  };
  return (
    <section className="bf-card space-y-2">
      <h2 className="text-sm font-medium">
        GPU telemetry (last 15 min)
      </h2>
      <p className="text-xs text-bf-muted">
        DCGM data persists across a host reboot, so this reveals GPU state right
        up to a freeze. Xid=0 + cool + VRAM not maxed, then an instant stop,
        points to a host-side kernel/driver deadlock — not a GPU fault.
      </p>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
          <tr>
            <th className="p-2">Metric</th>
            <th className="p-2">Last</th>
            <th className="p-2">Min</th>
            <th className="p-2">Max</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(labels).map((k) => {
            const s = summarize(series[k]);
            return (
              <tr key={k} className="border-t border-white/5">
                <td className="p-2">{labels[k]}</td>
                <td className="p-2 font-mono">{s.last ?? "—"}</td>
                <td className="p-2 font-mono text-bf-muted">{s.min ?? "—"}</td>
                <td className="p-2 font-mono text-bf-muted">{s.max ?? "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function HostDiagnosticsTab() {
  const triage = useQuery({
    queryKey: ["ops-triage"],
    queryFn: api.operations.triage,
  });
  const checklist = useQuery({
    queryKey: ["ops-checklist"],
    queryFn: api.operations.checklist,
  });
  const instrumentation = useQuery({
    queryKey: ["ops-instrumentation"],
    queryFn: api.operations.instrumentation,
  });
  const containers = useQuery({
    queryKey: ["ops-containers"],
    queryFn: api.operations.containers,
  });

  const t = triage.data;
  const verdict = t?.verdict ?? "unknown";
  const uptimeH =
    t?.uptime_seconds != null ? (t.uptime_seconds / 3600).toFixed(1) : null;

  return (
    <div className="space-y-4">
      {triage.isError && (
        <div className="bf-card border border-red-500/30 bg-red-500/5 text-sm text-red-300">
          Host diagnostics unavailable: {(triage.error as Error).message}
        </div>
      )}

      {/* Reboot verdict */}
      <section className={"bf-card space-y-2 border " + (VERDICT_STYLES[verdict] ?? "")}>
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium">
            Last shutdown:{" "}
            {verdict === "clean"
              ? "Clean"
              : verdict === "abrupt"
                ? "Abrupt (hard freeze / power loss)"
                : "Unknown"}
          </h2>
          <span className="text-xs text-bf-muted">
            {uptimeH != null ? `up ${uptimeH} h` : ""}
          </span>
        </div>
        {t?.detail && <p className="text-sm">{t.detail}</p>}
        {t && t.kernel_crash_markers.length > 0 && (
          <p className="text-sm text-amber-300">
            Kernel markers found: {t.kernel_crash_markers.join(", ")}
          </p>
        )}
        {t && t.kernel_crash_markers.length === 0 && verdict === "abrupt" && (
          <p className="text-sm text-bf-muted">
            No panic / OOM / thermal / lockup markers in the previous boot —
            consistent with a traceless hang.
          </p>
        )}
        {t?.note && (
          <p className="rounded-md bg-black/20 p-2 text-xs italic text-bf-muted">
            {t.note}
          </p>
        )}
      </section>

      <CrashWindowCard />

      <div className="grid gap-4 lg:grid-cols-2">
        <ChecklistCard
          title="Stability checklist"
          items={checklist.data?.items}
        />
        <ChecklistCard
          title="Crash-forensics readiness"
          items={instrumentation.data?.items}
        />
      </div>

      <ContainersCard containers={containers.data?.containers} />
    </div>
  );
}

// ---------------- Event log tab ----------------

const SEVERITY_STYLES: Record<EventSeverity, string> = {
  info: "text-bf-muted",
  warning: "text-amber-300",
  error: "text-red-400",
  critical: "text-red-300 font-semibold",
};

const SEVERITY_DOT: Record<EventSeverity, string> = {
  info: "bg-bf-muted",
  warning: "bg-amber-400",
  error: "bg-red-500",
  critical: "bg-red-400",
};

const PAGE_SIZE = 50;

// Service slug (events.service) -> docker container_name for log tail panel.
const LOG_CONTAINER_BY_SERVICE: Record<string, string> = {
  deepstream: "blinkfree-deepstream",
  preview: "blinkfree-preview",
  captioner: "blinkfree-captioner",
  track_aggregator: "blinkfree-track-aggregator",
  embedder: "blinkfree-embedder",
  db_writer: "blinkfree-db-writer",
  api: "blinkfree-api",
  janitor: "blinkfree-janitor",
  supervisor: "blinkfree-supervisor",
};

const LOG_CONTAINER_OPTIONS = Object.entries(LOG_CONTAINER_BY_SERVICE).map(
  ([service, container]) => ({ service, container }),
);

function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
}

function EventLogTab() {
  // Draft filters (form) vs applied filters (query key). Applying commits the
  // draft so typing doesn't refetch on every keystroke.
  const [service, setService] = useState("");
  const [severity, setSeverity] = useState("");
  const [eventType, setEventType] = useState("");
  const [cameraId, setCameraId] = useState("");
  const [text, setText] = useState("");
  const [fromLocal, setFromLocal] = useState("");
  const [toLocal, setToLocal] = useState("");
  const [applied, setApplied] = useState<EventFilters>({});

  const [paused, setPaused] = useState(false);
  const [olderRows, setOlderRows] = useState<EventRow[]>([]);
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [logContainer, setLogContainer] = useState("");

  const logTail = useQuery({
    queryKey: ["ops-docker-logs", logContainer],
    queryFn: () => api.operations.dockerLogs(logContainer, 200),
    enabled: logContainer.length > 0,
    refetchInterval: logContainer.length > 0 && !paused ? 15_000 : false,
  });

  const filtersKey = JSON.stringify(applied);

  // Reset the "load more" tail whenever the applied filters change.
  useEffect(() => {
    setOlderRows([]);
    setOlderCursor(null);
    setExpanded(new Set());
  }, [filtersKey]);

  const first = useQuery({
    queryKey: ["events", filtersKey],
    queryFn: () => api.events.list({ ...applied, limit: PAGE_SIZE }),
    refetchInterval: paused ? false : 10_000,
  });

  const apply = () => {
    setApplied({
      service: service || undefined,
      severity: severity || undefined,
      event_type: eventType || undefined,
      camera_id: cameraId || undefined,
      q: text || undefined,
      from_ts: toIso(fromLocal),
      to_ts: toIso(toLocal),
    });
  };

  const clear = () => {
    setService("");
    setSeverity("");
    setEventType("");
    setCameraId("");
    setText("");
    setFromLocal("");
    setToLocal("");
    setApplied({});
  };

  const rows = useMemo(
    () => [...(first.data?.events ?? []), ...olderRows],
    [first.data, olderRows],
  );

  const nextCursor = olderCursor ?? first.data?.next_cursor ?? null;

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await api.events.list({
        ...applied,
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      setOlderRows((prev) => [...prev, ...page.events]);
      setOlderCursor(page.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  };

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bf-card grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
        <label className="text-xs text-bf-muted">
          From
          <input
            type="datetime-local"
            className="bf-input mt-1"
            value={fromLocal}
            onChange={(e) => setFromLocal(e.target.value)}
          />
        </label>
        <label className="text-xs text-bf-muted">
          To
          <input
            type="datetime-local"
            className="bf-input mt-1"
            value={toLocal}
            onChange={(e) => setToLocal(e.target.value)}
          />
        </label>
        <label className="text-xs text-bf-muted">
          Service
          <input
            className="bf-input mt-1"
            value={service}
            placeholder="deepstream"
            onChange={(e) => setService(e.target.value)}
          />
        </label>
        <label className="text-xs text-bf-muted">
          Severity
          <select
            className="bf-input mt-1"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
          >
            <option value="">any</option>
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="error">error</option>
            <option value="critical">critical</option>
          </select>
        </label>
        <label className="text-xs text-bf-muted">
          Event type
          <input
            className="bf-input mt-1"
            value={eventType}
            placeholder="restart_escalation"
            onChange={(e) => setEventType(e.target.value)}
          />
        </label>
        <label className="text-xs text-bf-muted">
          Camera
          <input
            className="bf-input mt-1"
            value={cameraId}
            placeholder="cam3"
            onChange={(e) => setCameraId(e.target.value)}
          />
        </label>
        <label className="col-span-2 text-xs text-bf-muted md:col-span-3">
          Search reason
          <input
            className="bf-input mt-1"
            value={text}
            placeholder="text in reason…"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && apply()}
          />
        </label>
        <div className="col-span-2 flex items-end gap-2 md:col-span-3">
          <button type="button" className="bf-button" onClick={apply}>
            Apply
          </button>
          <button
            type="button"
            className="bf-button bg-white/10 bg-none"
            onClick={clear}
          >
            Clear
          </button>
          <button
            type="button"
            className="bf-button bg-white/10 bg-none"
            onClick={() => setPaused((p) => !p)}
            title={paused ? "Resume auto-refresh" : "Pause auto-refresh"}
          >
            {paused ? (
              <>
                <Play size={16} className="mr-1" /> Resume
              </>
            ) : (
              <>
                <Pause size={16} className="mr-1" /> Pause
              </>
            )}
          </button>
          <button
            type="button"
            className="bf-button bg-white/10 bg-none"
            onClick={() => first.refetch()}
            title="Refresh now"
          >
            <RefreshCw
              size={16}
              className={first.isFetching ? "animate-spin" : ""}
            />
          </button>
        </div>
        <label className="col-span-2 text-xs text-bf-muted xl:col-span-3">
          Container log tail
          <select
            className="bf-input mt-1 max-w-md"
            value={logContainer}
            onChange={(e) => setLogContainer(e.target.value)}
          >
            <option value="">— hidden —</option>
            {LOG_CONTAINER_OPTIONS.map(({ service, container }) => (
              <option key={container} value={container}>
                {service} ({container})
              </option>
            ))}
          </select>
        </label>
      </div>

      {first.isError && (
        <div className="bf-card border border-red-500/30 bg-red-500/5 text-sm text-red-300">
          Failed to load events: {(first.error as Error).message}
        </div>
      )}

      <div
        className={
          "grid gap-4 " + (logContainer ? "lg:grid-cols-[1fr_minmax(18rem,28rem)]" : "")
        }
      >
        <div className="bf-card overflow-x-auto p-0 min-w-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
              <tr>
                <th className="w-8 p-3"></th>
                <th className="p-3">Time</th>
                <th className="p-3">Severity</th>
                <th className="p-3">Service</th>
                <th className="p-3">Type</th>
                <th className="p-3">Camera</th>
                <th className="p-3">Reason</th>
                <th className="w-8 p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !first.isLoading && (
                <tr>
                  <td colSpan={8} className="p-4 text-center text-sm text-bf-muted">
                    No events match these filters.
                  </td>
                </tr>
              )}
              {rows.map((ev) => {
                const isOpen = expanded.has(ev.id);
                return (
                  <EventRowView
                    key={ev.id}
                    ev={ev}
                    isOpen={isOpen}
                    onToggle={() => toggle(ev.id)}
                    onServiceClick={() => {
                      const c = LOG_CONTAINER_BY_SERVICE[ev.service];
                      if (c) setLogContainer(c);
                    }}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {logContainer && (
          <aside className="bf-card flex min-h-[16rem] flex-col gap-2 lg:max-h-[70vh]">
            <div className="flex items-center justify-between gap-2">
              <h3 className="truncate text-sm font-medium">{logContainer}</h3>
              <button
                type="button"
                className="text-xs text-bf-muted hover:text-bf-text"
                onClick={() => logTail.refetch()}
              >
                Refresh
              </button>
            </div>
            {logTail.isLoading && (
              <div className="text-xs text-bf-muted">Loading log tail…</div>
            )}
            {logTail.isError && (
              <div className="text-xs text-red-300">
                {(logTail.error as Error).message || "Log tail failed"}
              </div>
            )}
            <pre className="min-h-0 flex-1 overflow-auto rounded-md bg-black/40 p-2 text-[11px] leading-relaxed text-bf-muted">
              {(logTail.data?.lines ?? []).join("\n") ||
                (logTail.data?.error ?? "No lines returned.")}
            </pre>
          </aside>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-bf-muted">
        <span>
          {rows.length} event{rows.length === 1 ? "" : "s"} shown
          {paused ? " · auto-refresh paused" : " · refreshing every 10s"}
        </span>
        {nextCursor && (
          <button
            type="button"
            className="bf-button bg-white/10 bg-none"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load older"}
          </button>
        )}
      </div>
    </div>
  );
}

function EventRowView({
  ev,
  isOpen,
  onToggle,
  onServiceClick,
}: {
  ev: EventRow;
  isOpen: boolean;
  onToggle: () => void;
  onServiceClick?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [lokiStub, setLokiStub] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(ev, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable */
    }
  };
  const hasDetails = ev.details && Object.keys(ev.details).length > 0;

  return (
    <>
      <tr className="border-t border-white/5 align-top">
        <td className="p-3">
          {hasDetails ? (
            <button
              type="button"
              onClick={onToggle}
              className="text-bf-muted hover:text-bf-text"
              aria-label={isOpen ? "collapse" : "expand"}
            >
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          ) : null}
        </td>
        <td className="whitespace-nowrap p-3 font-mono text-xs text-bf-muted">
          <button
            type="button"
            className="text-left hover:text-bf-text"
            title="Loki deep-link (phase 2 — not configured). Click to copy ISO timestamp."
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(ev.ts);
                setLokiStub(true);
                window.setTimeout(() => setLokiStub(false), 2500);
              } catch {
                /* clipboard unavailable */
              }
            }}
          >
            {new Date(ev.ts).toLocaleString()}
          </button>
          {lokiStub && (
            <div className="mt-0.5 text-[10px] text-bf-muted">
              Copied — Loki raw-log search planned (phase 2)
            </div>
          )}
        </td>
        <td className="p-3">
          <span className="flex items-center gap-2">
            <span
              className={"inline-block h-2 w-2 rounded-full " + SEVERITY_DOT[ev.severity]}
            />
            <span className={SEVERITY_STYLES[ev.severity]}>{ev.severity}</span>
          </span>
        </td>
        <td className="p-3">
          {onServiceClick && LOG_CONTAINER_BY_SERVICE[ev.service] ? (
            <button
              type="button"
              className="text-left hover:text-bf-violet"
              title="Show container log tail"
              onClick={onServiceClick}
            >
              {ev.service}
            </button>
          ) : (
            ev.service
          )}
        </td>
        <td className="p-3 font-mono text-xs">{ev.event_type}</td>
        <td className="p-3 text-xs">{ev.camera_id || "—"}</td>
        <td className="p-3">{ev.reason}</td>
        <td className="p-3 text-right">
          <button
            type="button"
            onClick={copy}
            className="text-bf-muted hover:text-bf-text"
            title="Copy row JSON"
          >
            <Copy size={14} />
          </button>
          {copied && <span className="ml-1 text-[10px] text-emerald-400">copied</span>}
        </td>
      </tr>
      {isOpen && hasDetails && (
        <tr className="border-t border-white/5 bg-black/20">
          <td></td>
          <td colSpan={7} className="p-3">
            <pre className="overflow-x-auto rounded-md bg-black/40 p-3 text-xs text-bf-muted">
              {JSON.stringify(ev.details, null, 2)}
              {ev.actor ? `\n\nactor: ${ev.actor}` : ""}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}
